import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  exchangeCodeForToken,
  getKakaoUserInfo,
  saveTokens,
} from '../services/kakaoAuth';
import { useAuthStore, mapKakaoUserToUser } from '../stores/authStore';
import { createOrUpdateUser, IssuedCoupon } from '../api/userApi';
import CompleteProfileModal from '../components/auth/CompleteProfileModal';
import WelcomeModal from '../components/auth/WelcomeModal';

const KakaoCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [error, setError] = useState<string>('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [issuedCoupons, setIssuedCoupons] = useState<IssuedCoupon[]>([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // 이미 처리했으면 중복 실행 방지
    if (hasProcessed.current) {
      return;
    }

    const handleKakaoCallback = async () => {
      // 처리 시작 표시
      hasProcessed.current = true;
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      // 에러 처리
      if (errorParam) {
        setError('로그인이 취소되었습니다.');
        localStorage.removeItem('kakao_login_redirect');
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      // 인가 코드가 없는 경우
      if (!code) {
        setError('인가 코드를 받지 못했습니다.');
        localStorage.removeItem('kakao_login_redirect');
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      try {
        const clientId = process.env.REACT_APP_KAKAO_CLIENT_ID || '';
        const redirectUri = process.env.REACT_APP_KAKAO_REDIRECT_URI || `${window.location.origin}/auth/kakao/callback`;

        // 1. 인가 코드로 액세스 토큰 교환
        const tokenResponse = await exchangeCodeForToken(code, {
          clientId,
          redirectUri,
        });

        // 2. 토큰 저장
        saveTokens(tokenResponse);

        // 3. 사용자 정보 조회
        const kakaoUser = await getKakaoUserInfo(tokenResponse.access_token);

        // 4. 사용자 정보를 앱 User 형식으로 변환
        const user = mapKakaoUserToUser(kakaoUser);

        // 5. 백엔드에 사용자 정보 저장 (전화번호와 실명 확인용) + JWT 토큰 받기
        const loginResponse = await createOrUpdateUser({
          kakao_id: String(kakaoUser.id),
          email: kakaoUser.kakao_account.email,
          nickname: kakaoUser.kakao_account.profile?.nickname,  // 카카오 프로필 닉네임
          name: undefined,  // 실명은 추가 입력 필요
          phone: undefined, // 카카오에서 전화번호는 별도 동의 필요
        });

        const backendUser = loginResponse.user;
        const jwtToken = loginResponse.access_token;

        // 6. 인증 스토어에 로그인 상태 저장 (JWT 토큰 사용)
        login({
          ...user,
          id: backendUser.id,  // 백엔드 DB의 실제 user ID 사용
          phone: backendUser.phone,
        }, jwtToken);  // JWT 토큰 저장

        // 7. 발급된 쿠폰 정보 및 신규 사용자 여부 저장
        console.log('Login response:', loginResponse);
        console.log('Issued coupons:', loginResponse.issued_coupons);
        console.log('Is new user:', loginResponse.is_new_user);

        // 신규 사용자 여부 설정
        const newUser = loginResponse.is_new_user || false;
        setIsNewUser(newUser);

        // 발급된 쿠폰이 있으면 저장
        if (loginResponse.issued_coupons && loginResponse.issued_coupons.length > 0) {
          console.log('Setting issued coupons:', loginResponse.issued_coupons);
          setIssuedCoupons(loginResponse.issued_coupons);
        }

        // 8. 프로필 완성 필요 여부 확인
        const profileIncomplete = !backendUser.phone || !backendUser.name || !backendUser.birth_date;
        setNeedsProfile(profileIncomplete);

        // 9. 프로필이 미완성이면 먼저 프로필 모달 표시
        if (profileIncomplete) {
          console.log('Profile incomplete, showing profile modal first');
          setShowProfileModal(true);
        } else if (newUser) {
          // 신규 사용자이고 프로필이 완성된 경우 환영 모달 표시
          console.log('New user with complete profile, showing welcome modal');
          setShowWelcomeModal(true);
        } else {
          // 기존 사용자이고 프로필 완성된 경우 원래 페이지 또는 홈으로
          console.log('Existing user with complete profile, navigating to redirect URL or home');
          const redirectUrl = localStorage.getItem('kakao_login_redirect') || '/';
          localStorage.removeItem('kakao_login_redirect');
          navigate(redirectUrl);
        }
      } catch (err) {
        console.error('Kakao login error:', err);
        const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
        console.error('Error details:', errorMessage);
        setError(`로그인 중 오류가 발생했습니다: ${errorMessage}`);
        localStorage.removeItem('kakao_login_redirect');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    };

    handleKakaoCallback();
  }, [searchParams, navigate, login]);

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          {error ? (
            <>
              <div className="mb-4">
                <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">로그인 실패</h2>
              <p className="text-gray-600">{error}</p>
            </>
          ) : (
            <>
              <div className="mb-4">
                <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">로그인 처리중</h2>
              <p className="text-gray-600">잠시만 기다려주세요...</p>
            </>
          )}
        </div>
      </div>

      {/* Welcome Modal - 신규 사용자 */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        issuedCoupons={issuedCoupons}
        onClose={() => {
          console.log('Welcome modal closed');
          setShowWelcomeModal(false);
          const redirectUrl = localStorage.getItem('kakao_login_redirect') || '/';
          localStorage.removeItem('kakao_login_redirect');
          navigate(redirectUrl);
        }}
      />

      {/* Profile Completion Modal */}
      <CompleteProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          console.log('Profile modal closed (skipped)');
          setShowProfileModal(false);
          // 신규 사용자면 환영 모달 표시, 아니면 원래 페이지로
          if (isNewUser) {
            console.log('New user, showing welcome modal after skipping profile');
            setShowWelcomeModal(true);
          } else {
            console.log('Existing user, navigating to redirect URL or home after skipping profile');
            const redirectUrl = localStorage.getItem('kakao_login_redirect') || '/';
            localStorage.removeItem('kakao_login_redirect');
            navigate(redirectUrl);
          }
        }}
        onComplete={() => {
          console.log('Profile modal completed');
          setShowProfileModal(false);
          // 신규 사용자면 환영 모달 표시, 아니면 원래 페이지로
          if (isNewUser) {
            console.log('New user, showing welcome modal after completing profile');
            setShowWelcomeModal(true);
          } else {
            console.log('Existing user, navigating to redirect URL or home after completing profile');
            const redirectUrl = localStorage.getItem('kakao_login_redirect') || '/';
            localStorage.removeItem('kakao_login_redirect');
            navigate(redirectUrl);
          }
        }}
      />
    </>
  );
};

export default KakaoCallbackPage;
